import { useEffect, useMemo, useState } from 'react';
import { roundRupees } from '../utils/money';
import { formatModelCode } from '../utils/modelCategoryRanges';
import {
  buildWholesaleLineFromInventory,
  getWholesaleQtyError,
  recalcWholesaleLine,
  WHOLESALE_CATEGORY_LABELS,
} from '../utils/wholesaleOrderForm';

const WholesaleModelSidePanel = ({
  item,
  existingLine = null,
  open,
  onClose,
  onAddToBucket,
  onRemoveFromBucket,
  hidePrice = false,
  showWholesaleCompare = false,
  t,
}) => {
  const [qty, setQty] = useState(1);
  const [colorRequest, setColorRequest] = useState('');
  const [showColorNote, setShowColorNote] = useState(false);

  useEffect(() => {
    if (!open || !item) return;
    setQty(existingLine?.qty ?? 1);
    setColorRequest(existingLine?.colorRequest ?? '');
    setShowColorNote(Boolean(existingLine?.colorRequest?.trim()));
  }, [open, item, existingLine]);

  useEffect(() => {
    if (!open) return;

    document.body.classList.add('overflow-hidden');

    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.classList.remove('overflow-hidden');
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  const draftLine = useMemo(() => {
    if (!item) return null;
    const base = existingLine
      ? { ...existingLine }
      : buildWholesaleLineFromInventory(item, qty);
    const recalced = recalcWholesaleLine(base, qty);
    return { ...recalced, colorRequest };
  }, [item, existingLine, qty, colorRequest]);

  const qtyError = draftLine ? getWholesaleQtyError(draftLine) : null;

  const formatMoney = (amount) => `₹${Number(amount || 0).toLocaleString('en-IN')}`;

  const handleQtyChange = (nextQty) => {
    const parsed = parseInt(nextQty, 10);
    setQty(Number.isNaN(parsed) ? 0 : parsed);
  };

  const stepQty = (delta) => {
    setQty((prev) => Math.max(0, prev + delta));
  };

  const handleAddToBucket = () => {
    if (!draftLine || qtyError) return;
    onAddToBucket({
      ...draftLine,
      colorRequest: colorRequest.trim(),
    });
  };

  if (!open || !item) return null;

  const isEditing = Boolean(existingLine);
  const wholesaleUnit = roundRupees(item.wholesalePrice ?? item.price);
  const retailUnit = roundRupees(item.retailPrice ?? item.price);
  const showRetailCompare = showWholesaleCompare && retailUnit > wholesaleUnit;
  const savingsPerUnit = showRetailCompare ? roundRupees(retailUnit - wholesaleUnit) : 0;
  const label = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);

  const renderPriceBlock = () => {
    if (showWholesaleCompare) {
      return (
        <div className="mt-1.5 sm:mt-2 space-y-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm">
            <span className="text-xs font-medium uppercase tracking-wide text-violet-700">
              {label('wholesaleBasket.wholesaleUnit', 'Wholesale')}
            </span>
            <span className="font-semibold text-violet-800">{formatMoney(wholesaleUnit)}</span>
            {showRetailCompare && (
              <>
                <span className="text-gray-400">·</span>
                <span className="text-xs text-gray-500">
                  {label('wholesaleBasket.retailUnit', 'Retail')}{' '}
                  <span className="line-through">{formatMoney(retailUnit)}</span>
                </span>
              </>
            )}
          </div>
          {showRetailCompare && savingsPerUnit > 0 && (
            <p className="text-xs font-medium text-green-700">
              {label('wholesaleBasket.youSave', 'You save {amount} per piece').replace(
                '{amount}',
                formatMoney(savingsPerUnit)
              )}
            </p>
          )}
          <p className="text-xs text-gray-500">
            {label('wholesaleBasket.stockLabel', 'Stock')} {item.currentStock}
          </p>
        </div>
      );
    }

    return (
      <p className="text-sm text-gray-700 mt-1.5 sm:mt-2">
        {!hidePrice && (
          <>
            <span className="font-semibold text-blue-700">{formatMoney(item.price)}</span>
            <span className="text-gray-400 mx-1.5">·</span>
          </>
        )}
        <span>
          {label('wholesaleBasket.stockLabel', 'Stock')} {item.currentStock}
        </span>
      </p>
    );
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        className="fixed top-0 right-0 h-full max-h-[100dvh] w-full sm:max-w-[90vw] sm:w-[36rem] bg-white shadow-2xl z-50 flex flex-col overflow-hidden md:left-auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby="wholesale-panel-title"
      >
        <div className="shrink-0 bg-white border-b border-gray-200 px-4 py-3 sm:px-6 sm:py-4 flex justify-between items-start">
          <h2 id="wholesale-panel-title" className="text-lg sm:text-xl font-bold text-gray-800 pr-4 leading-tight">
            {isEditing ? 'Edit bucket item' : 'Add to bucket'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-xl p-1 hover:bg-gray-100 rounded shrink-0"
            aria-label="Close panel"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-hidden flex flex-col gap-3 px-4 py-3 sm:px-6 sm:py-4">
          <div className="flex-1 min-h-0 min-h-[7rem] flex items-center justify-center bg-gray-50 rounded-lg overflow-hidden">
            {item.photo ? (
              <img
                src={item.photo}
                alt=""
                className="max-h-full max-w-full w-full object-contain p-2 sm:p-3"
              />
            ) : (
              <div className="w-full h-full min-h-[7rem] bg-gray-100 flex items-center justify-center text-gray-400 text-xs sm:text-sm">
                No photo
              </div>
            )}
          </div>

          <div className="shrink-0 min-w-0">
            <h3 className="text-base sm:text-xl font-bold text-gray-800 line-clamp-2">{item.modelName}</h3>
            <p className="text-sm text-gray-600">{formatModelCode(item.size, item.modelNumber)}</p>
            <p className="text-xs text-gray-500 mt-0.5">
              {WHOLESALE_CATEGORY_LABELS[item.size] || item.size || 'N/A'}
            </p>
            {renderPriceBlock()}
          </div>

          <div className="shrink-0 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-1 border-t border-gray-100">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Quantity</label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => stepQty(-1)}
                  disabled={qty <= 1}
                  className="admin-btn admin-btn-neutral h-9 w-9 p-0 text-lg disabled:opacity-40"
                >
                  −
                </button>
                <input
                  type="number"
                  value={qty}
                  onChange={(e) => handleQtyChange(e.target.value)}
                  min="1"
                  max={item.currentStock}
                  className={`h-9 w-16 min-w-[4rem] rounded-md border border-gray-300 bg-white text-gray-900 text-sm text-center px-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${qtyError ? 'border-red-500' : ''}`}
                />
                <button
                  type="button"
                  onClick={() => stepQty(1)}
                  disabled={qty >= item.currentStock}
                  className="admin-btn admin-btn-neutral h-9 w-9 p-0 text-lg disabled:opacity-40"
                >
                  +
                </button>
              </div>
              {qtyError ? (
                <p className="text-xs text-red-600 mt-1">{qtyError}</p>
              ) : (
                <p className="text-xs text-gray-500 mt-1">Max {item.currentStock}</p>
              )}
            </div>

            {draftLine && !hidePrice && !showWholesaleCompare && (
              <div className="text-sm sm:text-right shrink-0">
                <div>
                  <span className="text-gray-500">
                    {label('wholesaleBasket.lineTotal', 'Total')}{' '}
                  </span>
                  <span className="font-bold text-gray-900">{formatMoney(draftLine.totalValue)}</span>
                </div>
                {!showWholesaleCompare && !hidePrice && (
                  <div>
                    <span className="text-gray-500">Advance </span>
                    <span className="font-semibold text-gray-800">{formatMoney(draftLine.advance)}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="shrink-0">
            {showColorNote ? (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-gray-600">Color note</label>
                  <button
                    type="button"
                    onClick={() => {
                      setShowColorNote(false);
                      setColorRequest('');
                    }}
                    className="text-xs text-gray-500 hover:text-gray-700"
                  >
                    Remove
                  </button>
                </div>
                <textarea
                  value={colorRequest}
                  onChange={(e) => setColorRequest(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={2}
                  placeholder="Special color requirements…"
                />
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowColorNote(true)}
                className="text-xs font-medium text-blue-700 hover:text-blue-900"
              >
                + Add color note
              </button>
            )}
          </div>
        </div>

        <div className="shrink-0 border-t bg-white px-4 py-3 sm:px-6 sm:py-4 space-y-2 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <button
            type="button"
            onClick={handleAddToBucket}
            disabled={Boolean(qtyError) || qty <= 0}
            className="admin-btn admin-btn-submit w-full py-2.5 sm:py-3 text-sm sm:text-base font-semibold disabled:opacity-50"
          >
            {isEditing ? 'Update bucket item' : 'Add to order bucket'}
          </button>
          {isEditing && (
            <button
              type="button"
              onClick={() => {
                onRemoveFromBucket();
                onClose();
              }}
              className="admin-btn admin-btn-danger w-full py-2.5 text-sm font-semibold"
            >
              Remove from bucket
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default WholesaleModelSidePanel;
